import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import Select, { components, createFilter } from 'react-select';
import { useStoreState, useStoreActions } from 'easy-peasy';

import { httpUpdateUser } from "services/users"
import { httpGetTransactionsCount } from "services/transactions"
import { useAuthContext } from "contexts/AuthContext"
import { useNotification } from "stories/components/Notification"
import { logError } from "services/logger"
import { ALL_PORTFOLIOS, selectionSummary, sortPortfolios, toggleSelection } from "store/activePortfolios"
import "./ActivePortfolioSelect.css"

// Top-left "Valitse salkku" selector of the active portfolios (#1409). Built like StockMultiSelect
// (#2344): a one-line summary instead of chips, a checkbox in each option. Changes are a draft
// while the menu is open and are taken into use when it closes (click outside, Esc, Tab), so the
// views fetch once rather than on every click. At least one portfolio stays selected.

const sameSelection = (a, b) =>
  Boolean(a.all) === Boolean(b.all)
  && a.target === b.target
  && a.ids.length === b.ids.length
  && a.ids.every((id, i) => id === b.ids[i])

const ValueContainer = ({ children, ...props }) => {
  const { getValue, selectProps } = props
  // react-select passes [placeholder-or-values, input]
  const [, input] = children

  if (getValue().length === 0) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>
  }

  // While the user types, the search text replaces the summary.
  return (
    <components.ValueContainer {...props}>
      {!selectProps.inputValue && (
        <div className="active-portfolio-select__summary" title={selectProps.summary}>{selectProps.summary}</div>
      )}
      {input}
    </components.ValueContainer>
  )
}

const Option = (props) => {
  const { data, isSelected } = props
  return (
    <components.Option {...props} className={data.value === ALL_PORTFOLIOS ? "active-portfolio-select__option--all" : undefined}>
      <input type="checkbox" className="active-portfolio-select__checkbox" checked={isSelected} readOnly tabIndex={-1} aria-hidden="true" />
      <span className="active-portfolio-select__name">{data.name}</span>
      {data.currency && <span className="active-portfolio-select__currency">{data.currency}</span>}
    </components.Option>
  )
}

// "Kaikki salkut" stays visible whatever is typed.
const nameFilter = createFilter({ stringify: (option) => option.data.name || "" })
const filterOption = (option, input) => option.value === ALL_PORTFOLIOS || nameFilter(option, input)

const menuPortalTarget = typeof document !== "undefined" ? document.body : null

const ActivePortfolioSelect = ({ id = "active-portfolio-select" }) => {
  const { t } = useTranslation();
  const { myUser } = useAuthContext()

  const portfolios = useStoreState((state) => state.portfolio?.portfolios);
  const activePortfolios = useStoreState((state) => state.portfolio?.activePortfolios);
  const allSelected = useStoreState((state) => state.portfolio?.allSelected);
  const portfolio = useStoreState((state) => state.portfolio?.portfolio);
  const loading = useStoreState((state) => state.portfolio?.loading);
  const loaded = useStoreState((state) => state.portfolio?.loaded);
  const loadError = useStoreState((state) => state.portfolio?.loadError);
  const getPortfolios = useStoreActions((actions) => actions.portfolio.getPortfolios);
  const commitSelection = useStoreActions((actions) => actions.portfolio.commitSelection);
  const setPortfolioSummary = useStoreActions((actions) => actions.portfolioSummary.setPortfolioSummary);
  const setPortfolioSummaryPortfolios = useStoreActions((actions) => actions.portfolioSummary.setPortfolio);

  const sorted = useMemo(() => sortPortfolios(portfolios), [portfolios])

  const committed = useMemo(() => {
    const ids = (activePortfolios || []).map((p) => p.id)
    return { ids, all: Boolean(allSelected), target: ids.includes(portfolio?.id) ? portfolio.id : ids[0] ?? null }
  }, [activePortfolios, allSelected, portfolio?.id])

  // The selection being edited while the menu is open. A ref too, because the menu can close in
  // the same tick as the last change.
  const [draft, setDraftState] = useState(null)
  const draftRef = useRef(null)
  const setDraft = (next) => {
    draftRef.current = next
    setDraftState(next)
  }
  const [blocked, setBlocked] = useState(false)
  const [liveMessage, setLiveMessage] = useState("")

  const shown = draft || committed
  const total = sorted.length

  const summary = useMemo(() => selectionSummary({
    active: sorted.filter((p) => shown.ids.includes(p.id)),
    all: shown.all,
    total,
  }, t), [sorted, shown, total, t])

  const options = useMemo(() => {
    const list = sorted.map((p) => ({
      value: p.id,
      label: `${p.name} (${p.currency_label})`,
      name: p.name,
      currency: p.currency_label,
    }))
    // With one portfolio "Kaikki salkut" would only repeat it.
    if (list.length < 2) {
      return list
    }
    const all = t("active_portfolios_all", "Kaikki salkut ({{count}})", { count: list.length })
    return [{ value: ALL_PORTFOLIOS, label: all, name: all }, ...list]
  }, [sorted, t])

  const value = useMemo(() => {
    if (shown.all) {
      return options
    }
    return options.filter((option) => shown.ids.includes(option.value))
  }, [options, shown])

  const handleMenuOpen = () => {
    setBlocked(false)
    setDraft(committed)
  }

  const handleChange = (selected, { option }) => {
    if (!option) {
      return
    }
    const next = toggleSelection(draftRef.current || committed, option.value, sorted)
    setBlocked(next.blocked)
    if (!next.blocked) {
      setDraft({ ids: next.ids, all: next.all, target: next.target })
    }
  }

  const handleMenuClose = () => {
    const next = draftRef.current
    setDraft(null)
    if (!next || next.ids.length === 0 || sameSelection(next, committed)) {
      return
    }
    const result = commitSelection(next)
    setPortfolioSummary([])
    setPortfolioSummaryPortfolios([])
    setLiveMessage(t("active_portfolios_live", "Aktiiviset salkut: {{summary}}", {
      summary: selectionSummary({ active: sorted.filter((p) => result.ids.includes(p.id)), all: result.all, total }, t),
    }))

    // The server keeps one active portfolio: the target, so another device opens at least that.
    if (myUser?.id && result.target?.id) {
      httpUpdateUser(myUser.id, { active_portfolio: result.target.id }).catch((error) => {
        logError(error?.data?.detail || error)
      })
    }
  }

  const initialLoad = loading && !loaded
  const empty = !initialLoad && total === 0

  let placeholder = ""
  if (initialLoad) {
    placeholder = t("active_portfolios_loading", "Ladataan salkkuja…")
  } else if (empty) {
    placeholder = t("active_portfolios_empty_short", "Ei salkkuja")
  }

  return (
    <div className="active-portfolio-select">
      <Select
        inputId={`${id}-input`}
        className="active-portfolio-select__container"
        classNamePrefix="active-portfolio-select"
        name="active_portfolios"
        aria-label={t("active_portfolios_aria", "Aktiiviset salkut")}
        isMulti
        isClearable={false}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        backspaceRemovesValue={false}
        tabSelectsValue={false}
        isDisabled={initialLoad}
        isLoading={initialLoad}
        options={options}
        value={initialLoad ? [] : value}
        onChange={handleChange}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        filterOption={filterOption}
        placeholder={placeholder}
        noOptionsMessage={() => t("active_portfolios_empty", "Ei salkkuja. Luo salkku Salkut-sivulla.")}
        components={{ ValueContainer, Option, ClearIndicator: null, IndicatorSeparator: null }}
        summary={summary}
        menuPortalTarget={menuPortalTarget}
        menuPlacement="auto"
        styles={{
          container: (provided) => ({ ...provided, width: '100%', minWidth: 0 }),
          menuPortal: (provided) => ({ ...provided, zIndex: 1060 }),
          menu: (provided) => ({ ...provided, maxWidth: 'calc(100vw - 16px)' }),
          menuList: (provided) => ({ ...provided, maxHeight: 300 }),
        }}
      />
      {blocked && (
        <div className="active-portfolio-select__status" role="status">
          <i className="fas fa-info-circle mr-1" aria-hidden="true" />
          {t("active_portfolios_min_one", "Valitse vähintään yksi salkku.")}
        </div>
      )}
      {loadError && (
        <div className="active-portfolio-select__status" role="status">
          <i className="fas fa-exclamation-triangle mr-1" aria-hidden="true" />
          {t("active_portfolios_error", "Salkkuja ei voitu ladata.")}{" "}
          <button type="button" className="btn btn-link p-0 active-portfolio-select__retry" onClick={() => getPortfolios()}>
            {t("active_portfolios_retry", "Yritä uudelleen")}
          </button>
        </div>
      )}
      <span className="sr-only" role="status" aria-live="polite">{liveMessage}</span>
    </div>
  )
}

/**
 * Side effects of the active selection that must run once, whether or not a selector is on
 * screen (on a phone the menu is usually closed). The Sidebar calls this.
 * - The transaction count the forms check the license limit against follows the target
 *   portfolio, as PortfolioSelectStore's did.
 * - A notice when the stored selection lost portfolios at startup.
 */
export const useActivePortfolioEffects = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const portfolioId = useStoreState((state) => state.portfolio?.portfolio?.id);
  const pruned = useStoreState((state) => state.portfolio?.pruned);
  const clearPruned = useStoreActions((actions) => actions.portfolio.clearPruned);
  const setCount = useStoreActions((actions) => actions.purchases.setCount);

  useEffect(() => {
    httpGetTransactionsCount({ portfolio: portfolioId, limit: 99999999, offset: 0 }).then(res => {
      setCount(res?.data)
    }, () => {})
  }, [portfolioId, setCount])

  useEffect(() => {
    if (pruned) {
      notify({ message: t("active_portfolios_pruned", "Osa valituista salkuista ei ole enää käytettävissä. Valinta päivitettiin.") })
      clearPruned()
    }
  }, [pruned, clearPruned, notify, t])
}

export default ActivePortfolioSelect;
